import React, { useState, useEffect } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useNavigate, useLocation } from "react-router-dom";
import { Box } from "@mui/material";

const isRouteActive = (route, pathname) => {
  // Check if current path matches the route itself
  if (pathname === route.route) {
    return true;
  }
  
  // Check if current path starts with the route (for nested routes)
  if (route.route && pathname.startsWith(route.route)) {
    return true;
  }
  
  // Check subRoutes for active state
  if (route.subRoutes) {
    return route.subRoutes.some((subRoute) => 
      pathname === subRoute.route || pathname.startsWith(subRoute.route)
    );
  }
  
  return false;
};

export default function NavBarItems({ routes, open, openDrawer, closeDrawer }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    // Find the active route and expand its parent if it's a subroute
    const activeRoute = routes.find((route) => {
      if (route.subRoutes) {
        return route.subRoutes.some((subRoute) => 
          location.pathname === subRoute.route || location.pathname.startsWith(subRoute.route)
        );
      }
      return false;
    });
    
    if (activeRoute) {
      setExpanded(activeRoute.key);
    }
  }, [location.pathname, routes]);

  const toggleExpand = (key) => {
    setExpanded((prevKey) => (prevKey === key ? null : key));
  };

  const handleRouteClick = (route, hasSubScreens) => {
    if (!open && hasSubScreens) {
      openDrawer(); // open the drawer to show submenu
    } else if (!hasSubScreens) {
      navigate(route.route);
      closeDrawer(); // close after navigating to direct route
    } else if (hasSubScreens) {
      toggleExpand(route.key);
      
      // If the route has a direct path, navigate to it when clicking the parent
      if (route.route) {
        navigate(route.route);
      }
    }
  };

  return (
    <List>
      {routes.filter(route => route.type === "collapse").map((route) => {
        const hasSubScreens = route.subRoutes?.length > 0;
        const isActive = isRouteActive(route, location.pathname);
        const isExpanded = expanded === route.key;

        return (
          <React.Fragment key={route.key}>
            <ListItem disablePadding sx={{ display: "block", position: "relative" }}>
              <ListItemButton
                onClick={() => handleRouteClick(route, hasSubScreens)}
                sx={{
                  borderRadius: "12px",
                  minHeight: 48,
                  marginTop: 1,
                  marginLeft: 1.5,
                  marginRight: 1.5,
                  paddingLeft: 3,
                  paddingRight: 3,
                  backgroundColor: isActive ? "secondary.main" : "light.main",
                  color: "#161616",
                  fontWeight: 800,
                  "&:hover": {
                    backgroundColor: isActive ? "secondary.main" : "lightred.main",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 20, mr: open ? 2 : "auto", ml: open ? 1 : -1.3 }}>
                  {route.icon}
                </ListItemIcon>
                <ListItemText primary={route.name} sx={{ opacity: open ? 1 : 0,fontWeight:600 }} />
                {hasSubScreens && open && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
            </ListItem>

            {hasSubScreens && open && (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {route.subRoutes.map((subRoute) => {
                    if (subRoute.type === "hidden") return null;
                    const isSubActive = location.pathname === subRoute.route || 
                                       location.pathname.startsWith(subRoute.route);
                    return (
                      <ListItemButton
                        key={subRoute.key}
                        onClick={() => {
                          navigate(subRoute.route);
                          closeDrawer(); // Close drawer after selecting sub menu
                        }}
                        sx={{
                          borderRadius: "12px",
                          minHeight: 48,
                          marginTop: 0.5,
                          marginLeft: 1.5,
                          marginRight: 1.5,
                          paddingLeft: 8,
                          paddingRight: 3,
                          backgroundColor: isSubActive ? "secondary.main" : "light.main",
                          color: "#161616",
                          fontWeight: 800,
                          "&:hover": { backgroundColor: "lightred.main" },
                        }}
                      >
                        <ListItemText primary={subRoute.name} sx={{ marginLeft: 1,fontWeight:600 }} />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </List>
  );
}